import { useId, type ReactNode } from 'react';
import { paletteById } from '../lib/palettes';

type SubjectKey = 'good_neighbor' | 'fit_for_life' | 'money_mastery' | 'bootstrap_boss' | 'generic';

type SubjectData = {
  bg: string;
  accent: string;
  illustration: ReactNode;
};

function fitFontSize(text: string, baseSize: number, comfortableLen: number): number {
  if (text.length <= comfortableLen) return baseSize;
  const scaled = baseSize * (comfortableLen / text.length);
  return Math.max(7, Math.round(scaled * 10) / 10);
}

function isLightHex(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

function darken(hex: string, amount = 0.18): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = 1 - amount;
  const to = (n: number) => Math.max(0, Math.min(255, Math.round(n * f))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

const PATCH_SUBJECTS: Record<SubjectKey, SubjectData> = {
  good_neighbor: {
    bg: '#1F3A55',
    accent: '#A0392C',
    illustration: (
      <g>
        <defs>
          <linearGradient id="sky-gn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2A4A6A" />
            <stop offset="100%" stopColor="#15273C" />
          </linearGradient>
        </defs>
        <rect x="-60" y="-50" width="120" height="100" fill="url(#sky-gn)" />
        <ellipse cx="-44" cy="-12" rx="10" ry="14" fill="#2D5A3D" />
        <rect x="-46" y="-2" width="4" height="10" fill="#3A2A1A" />
        <rect x="-32" y="-5" width="18" height="20" fill="#D9C268" />
        <polygon points="-32,-5 -23,-15 -14,-5" fill="#8C2A20" />
        <rect x="-26" y="3" width="5" height="8" fill="#3A2A1A" />
        <rect x="-19" y="0" width="4" height="4" fill="#1F3A55" />
        <rect x="-12" y="-12" width="28" height="28" fill="#F1E7D0" />
        <polygon points="-14,-12 2,-26 18,-12" fill="#8C2A20" />
        <rect x="-1" y="-2" width="6" height="14" fill="#5A3A22" />
        <rect x="1" y="-22" width="2" height="8" fill="#14202B" />
        <rect x="-1.5" y="-19" width="7" height="2" fill="#14202B" />
        <rect x="-9" y="-6" width="5" height="5" fill="#C58A2C" />
        <rect x="9" y="-6" width="5" height="5" fill="#C58A2C" />
        <rect x="18" y="-3" width="16" height="18" fill="#3B6B4C" />
        <polygon points="18,-3 26,-12 34,-3" fill="#8C2A20" />
        <rect x="23" y="5" width="5" height="10" fill="#3A2A1A" />
        <g fill="#F1E7D0">
          <rect x="-44" y="14" width="2" height="8" />
          <rect x="-40" y="14" width="2" height="8" />
          <rect x="-36" y="14" width="2" height="8" />
          <rect x="32" y="14" width="2" height="8" />
          <rect x="36" y="14" width="2" height="8" />
        </g>
        <rect x="38" y="-8" width="1.5" height="22" fill="#14202B" />
        <rect x="35" y="-12" width="7" height="6" fill="#C58A2C" rx="1" />
        <rect x="-60" y="22" width="120" height="20" fill="#2A2A2A" />
        <rect x="-3" y="28" width="6" height="3" fill="#F1E7D0" />
        <rect x="-3" y="35" width="6" height="3" fill="#F1E7D0" />
        <path d="M0 30 C-6 22, -14 26, -8 34 L0 42 L8 34 C14 26, 6 22, 0 30 Z" fill="#A0392C" stroke="#6E261D" strokeWidth="1" />
      </g>
    ),
  },
  fit_for_life: {
    bg: '#6E261D',
    accent: '#C58A2C',
    illustration: (
      <g>
        <circle cx="0" cy="0" r="50" fill="#8C2A20" />
        <polygon points="-50,30 -20,-15 0,10 25,-25 50,30" fill="#3A1410" />
        <polygon points="-50,30 -25,5 -10,20 50,30" fill="#1F0A08" />
        <circle cx="-20" cy="-20" r="10" fill="#F4C45A" />
        <g transform="translate(0,5)">
          <rect x="-22" y="-3" width="44" height="6" fill="#F1E7D0" rx="1" />
          <rect x="-28" y="-8" width="8" height="16" fill="#F1E7D0" rx="2" />
          <rect x="20" y="-8" width="8" height="16" fill="#F1E7D0" rx="2" />
        </g>
      </g>
    ),
  },
  money_mastery: {
    bg: '#1E3A2A',
    accent: '#C58A2C',
    illustration: (
      <g>
        <circle cx="0" cy="0" r="50" fill="#2D5A3D" />
        <g stroke="#C58A2C" strokeWidth="1.5" opacity="0.4">
          <line x1="0" y1="-45" x2="0" y2="-30" />
          <line x1="32" y1="-32" x2="22" y2="-22" />
          <line x1="45" y1="0" x2="30" y2="0" />
          <line x1="32" y1="32" x2="22" y2="22" />
          <line x1="0" y1="45" x2="0" y2="30" />
          <line x1="-32" y1="32" x2="-22" y2="22" />
          <line x1="-45" y1="0" x2="-30" y2="0" />
          <line x1="-32" y1="-32" x2="-22" y2="-22" />
        </g>
        <circle cx="0" cy="0" r="22" fill="#C58A2C" stroke="#8B5E1A" strokeWidth="2" />
        <circle cx="0" cy="0" r="18" fill="none" stroke="#8B5E1A" strokeWidth="1" />
        <text x="0" y="7" textAnchor="middle" fontFamily="Bevan, serif" fontSize="22" fill="#3A2510">$</text>
        <path d="M0 22 Q-6 28 -10 36" stroke="#1A3520" strokeWidth="2" fill="none" />
        <ellipse cx="-10" cy="36" rx="4" ry="2" fill="#3A6B4C" transform="rotate(-30 -10 36)" />
        <ellipse cx="-6" cy="30" rx="3" ry="1.5" fill="#3A6B4C" transform="rotate(-45 -6 30)" />
      </g>
    ),
  },
  bootstrap_boss: {
    bg: '#3A2510',
    accent: '#C58A2C',
    illustration: (
      <g>
        <circle cx="0" cy="0" r="50" fill="#5A3A22" />
        <path d="M -50 -50 L 50 -50 L 50 -8 Q 0 -16 -50 -8 Z" fill="#D9A85A" opacity="0.4" />
        <circle cx="22" cy="-26" r="8" fill="#F4C45A" opacity="0.85" />
        <ellipse cx="0" cy="32" rx="46" ry="14" fill="#3A1A0E" />
        <ellipse cx="0" cy="28" rx="34" ry="10" fill="#5A2E18" />
        <path d="M -16 8 L -20 28 L 20 28 L 16 8 Z" fill="#A0392C" stroke="#5A1810" strokeWidth="1.5" />
        <rect x="-18" y="6" width="36" height="5" fill="#8C2A20" stroke="#5A1810" strokeWidth="1" />
        <path d="M 0 8 Q 0 -8 -2 -22" stroke="#3A6B4C" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M -2 -14 Q -14 -16 -16 -8 Q -8 -8 -2 -14 Z" fill="#5A8B4C" stroke="#2D5A3D" strokeWidth="1" />
        <path d="M 0 -8 Q 12 -10 14 -2 Q 6 -2 0 -8 Z" fill="#5A8B4C" stroke="#2D5A3D" strokeWidth="1" />
        <circle cx="-2" cy="-22" r="3.5" fill="#C58A2C" stroke="#8B5E1A" strokeWidth="1" />
        <path d="M -2 -22 L -2 -28" stroke="#3A6B4C" strokeWidth="1.5" />
        <path d="M -8 -32 L -2 -36 L 4 -32" stroke="#C58A2C" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
      </g>
    ),
  },
  generic: {
    bg: '#1E3A2A',
    accent: '#C58A2C',
    illustration: (
      <g>
        <circle cx="0" cy="0" r="48" fill="#2D5A3D" />
        <polygon points="0,-24 7,-7 25,-7 11,4 16,21 0,11 -16,21 -11,4 -25,-7 -7,-7"
          fill="#C58A2C" stroke="#8B5E1A" strokeWidth="1.5" />
      </g>
    ),
  },
};

type PatchProps = {
  subject?: string | null;
  topText?: string;
  bottomText?: string;
  size?: number;
  locked?: boolean;
  imageUrl?: string | null;
  palette?: string | null;
};

export function Patch({ subject = 'generic', topText = '', bottomText = '', size = 200, locked = false, imageUrl = null, palette = null }: PatchProps) {
  const data = PATCH_SUBJECTS[subject as SubjectKey] ?? PATCH_SUBJECTS.generic;
  const id = useId();
  const topR = 84;
  const botR = 84;

  const paletteHex = palette ? paletteById(palette).hex : null;
  const rimColor = paletteHex ? darken(paletteHex, 0.15) : (data.accent === '#C58A2C' ? '#3B6B4C' : '#2A4A3A');
  const innerBgColor = paletteHex ?? data.bg;
  const textColor = paletteHex && isLightHex(paletteHex) ? '#14100A' : '#F1E7D0';

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'inline-block',
        filter: locked ? 'grayscale(0.85) brightness(0.55)' : 'none',
      }}
    >
      <svg
        viewBox="-110 -110 220 220"
        width={size}
        height={size}
        style={{
          filter: locked ? 'none' : 'drop-shadow(0 4px 8px rgba(0,0,0,0.35)) drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
        }}
      >
        <defs>
          <path id={`top-${id}`} d={`M ${-topR} 0 A ${topR} ${topR} 0 0 1 ${topR} 0`} />
          <path id={`bot-${id}`} d={`M ${-botR} 5 A ${botR} ${botR} 0 0 0 ${botR} 5`} />
          <radialGradient id={`bg-${id}`} cx="0.5" cy="0.5" r="0.6">
            <stop offset="0%" stopColor={innerBgColor} stopOpacity="1" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.4" />
          </radialGradient>
          <filter id={`felt-${id}`}>
            <feTurbulence type="fractalNoise" baseFrequency="2" numOctaves="2" seed="3" />
            <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0" />
            <feComposite in2="SourceGraphic" operator="in" />
          </filter>
          <clipPath id={`inner-${id}`} clipPathUnits="userSpaceOnUse">
            <circle cx="0" cy="0" r="70" />
          </clipPath>
        </defs>

        <circle cx="0" cy="0" r="105" fill={rimColor} />
        <circle cx="0" cy="0" r="105" fill="none" stroke="#1A2A1F" strokeWidth="2" />
        <circle cx="0" cy="0" r="98" fill={`url(#bg-${id})`} />
        <circle cx="0" cy="0" r="98" fill="#fff" filter={`url(#felt-${id})`} opacity="0.5" />

        <g clipPath={`url(#inner-${id})`}>
          {imageUrl ? (
            <image
              href={imageUrl}
              x={-70}
              y={-70}
              width={140}
              height={140}
              preserveAspectRatio="xMidYMid slice"
            />
          ) : (
            data.illustration
          )}
        </g>

        <circle cx="0" cy="0" r="72" fill="none" stroke="#F1E7D0" strokeWidth="1.5" strokeDasharray="3 2" opacity="0.7" />
        <circle cx="0" cy="0" r="68" fill="none" stroke={data.accent} strokeWidth="1" opacity="0.5" />

        {topText && (
          <text
            fontFamily="Bevan, serif"
            fontSize={fitFontSize(topText, 14, 18)}
            fill={textColor}
            letterSpacing={topText.length > 18 ? '1' : '2'}
          >
            <textPath href={`#top-${id}`} startOffset="50%" textAnchor="middle">
              {topText}
            </textPath>
          </text>
        )}
        {bottomText && (
          <text
            fontFamily="Bevan, serif"
            fontSize={fitFontSize(bottomText, 11, 22)}
            fill={textColor}
            letterSpacing={bottomText.length > 22 ? '1' : '1.5'}
          >
            <textPath href={`#bot-${id}`} startOffset="50%" textAnchor="middle">
              {bottomText}
            </textPath>
          </text>
        )}

        <circle cx="0" cy="0" r="100" fill="none" stroke="#F1E7D0" strokeWidth="1" strokeDasharray="2 2" opacity="0.4" />

        {locked && (
          <g>
            <circle cx="0" cy="0" r="100" fill="#000" opacity="0.4" />
            <g>
              <rect x="-14" y="-4" width="28" height="22" rx="3" fill="#F1E7D0" />
              <path d="M-9 -4 V -12 A 9 9 0 0 1 9 -12 V -4" stroke="#F1E7D0" strokeWidth="3" fill="none" />
              <circle cx="0" cy="6" r="2.5" fill="#14202B" />
              <rect x="-1" y="6" width="2" height="6" fill="#14202B" />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
